import { useState, useEffect } from 'react';
import { Building2, Plus, Pencil, Trash2, X, Check, User, Phone, Mail, Calendar, Link as LinkIcon, Search } from 'lucide-react';
import { Client, Contact, clientsRef, BUSINESS_SECTORS, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';
import { Dialog } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<Partial<Client>>({
    brand_name: '',
    contacts: [],
    sector: '',
    notes: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      const clientsData = await getDocuments<Client>(clientsRef);
      console.log('Loaded clients:', clientsData);
      setClients(clientsData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading clients:', error);
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setFormData({
      brand_name: '',
      contacts: [],
      sector: '',
      notes: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient?.id) {
        await updateDocument(clientsRef, editingClient.id, formData);
      } else { 
        await createDocument(clientsRef, {
          brand_name: formData.brand_name,
          contacts: formData.contacts || [],
          sector: formData.sector || '',
          notes: formData.notes || '',
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Client);
      }
      await loadClients();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (!clientToDelete?.id) return;
      await deleteDocument(clientsRef, clientToDelete.id);
      await loadClients();
      setIsDeleteModalOpen(false);
      setClientToDelete(null);
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      brand_name: client.brand_name,
      contacts: [...client.contacts],
      sector: client.sector,
      notes: client.notes
    });
    setIsModalOpen(true);
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), {
        name: '',
        email: '',
        phone: '',
        type: 'brand',
        role: ''
      }]
    }));
  };

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.filter((_, i) => i !== index) || []
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      ) || []
    }));
  };

  const handleViewDetails = (client: Client, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const filteredClients = clients.filter(client => {
    const searchLower = search.toLowerCase();
    return (
      client.brand_name.toLowerCase().includes(searchLower) ||
      client.contacts.some(contact => 
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email.toLowerCase().includes(searchLower) ||
        (contact.phone && contact.phone.toLowerCase().includes(searchLower))
      ) ||
      (client.notes && client.notes.toLowerCase().includes(searchLower))
    );
  });

  // Get current clients
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Clients</h1>
            <p className="text-gray-400">Gérez votre portefeuille clients</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un client
        </button>
      </div>

      <div className="bg-dark-50 rounded-xl border border-dark-200 overflow-hidden">
        <div className="p-4 border-b border-dark-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-dark-200 border border-dark-300 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : !filteredClients || filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-dark-200 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {search ? 'Aucun résultat trouvé' : 'Aucun client'}
            </h3>
            <p className="text-gray-400">
              {search ? 'Essayez de modifier vos critères de recherche' : 'Commencez par ajouter votre premier client'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-dark-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Contacts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-200">
                {currentClients.map((client) => (
                  <tr key={client.id} className="hover:bg-dark-100/50">
                    <td 
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetails(client)}
                    >
                      <div>
                        <div className="text-white font-medium">{client.brand_name}</div>
                        {client.sector?.main && (
                          <>
                            <div className="text-sm text-gray-400">
                              {BUSINESS_SECTORS.find(s => s.value === client.sector.main)?.label}
                              {client.sector.sub && (
                                <>
                                  {' • '}
                                  {BUSINESS_SECTORS
                                    .find(s => s.value === client.sector.main)
                                    ?.subcategories.find(sub => sub.value === client.sector.sub)?.label}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td 
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetails(client)}
                    >
                      <div className="space-y-1">
                        {client.contacts.map((contact, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            {contact.type === 'brand' ? (
                              <Building2 className="w-4 h-4 text-accent-blue" />
                            ) : (
                              <User className="w-4 h-4 text-accent-purple" />
                            )}
                            <span className="text-white">{contact.name}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-400">{contact.email}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td 
                      className="px-6 py-4 text-gray-400 cursor-pointer"
                      onClick={() => handleViewDetails(client)}
                    >
                      {client.notes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(client)}
                        className="text-accent-blue hover:text-accent-blue/80 mr-3"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setClientToDelete(client);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 p-4 border-t border-dark-200">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg bg-dark-200 text-white hover:bg-dark-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(number => {
                    if (number <= 3) return true;
                    if (number === totalPages) return true;
                    if (number === currentPage) return true;
                    if (number === currentPage - 1 || number === currentPage + 1) return true;
                    return false;
                  })
                  .map((number, index, array) => {
                    if (index > 0 && array[index - 1] !== number - 1) {
                      return (
                        <span key={`ellipsis-${number}`} className="text-gray-400 px-2">...</span>
                      );
                    }
                    return (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-1 rounded-lg ${
                          currentPage === number
                            ? 'bg-accent-blue text-white'
                            : 'bg-dark-200 text-white hover:bg-dark-300'
                        }`}
                      >
                        {number}
                      </button>
                    );
                  })}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg bg-dark-200 text-white hover:bg-dark-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
              <div className="absolute top-4 right-4">
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">
                    {editingClient ? 'Modifier le client' : 'Ajouter un client'}
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="brand_name" className="block text-sm font-medium text-gray-200">
                        Nom
                      </label>
                      <input
                        type="text"
                        id="brand_name"
                        value={formData.brand_name}
                        onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                        className="mt-1 block w-full rounded-lg bg-dark-200 border-dark-300 text-white focus:outline-none focus:ring-2 focus:ring-accent-blue mb-4"
                        placeholder="Nom de la marque ou de l'agence"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-1">
                        Secteur principal
                      </label>
                      <select
                        value={formData.sector?.main || ''}
                        onChange={(e) => {
                          const mainSector = e.target.value;
                          const sector = BUSINESS_SECTORS.find(s => s.value === mainSector);
                          setFormData({
                            ...formData,
                            sector: {
                              main: mainSector,
                              sub: ''
                            }
                          });
                        }}
                        className="w-full bg-dark-200 border border-dark-300 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                      >
                        <option value="">Sélectionner un secteur</option>
                        {BUSINESS_SECTORS.map((sector) => (
                          <option key={sector.value} value={sector.value}>{sector.label}</option>
                        ))}
                      </select>
                      
                      {formData.sector?.main && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-200 mb-1">
                            Sous-catégorie
                          </label>
                          <select
                            value={formData.sector.sub || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              sector: {
                                ...formData.sector,
                                sub: e.target.value
                              }
                            })}
                            className="w-full bg-dark-200 border border-dark-300 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
                          >
                            <option value="">Sélectionner une sous-catégorie</option>
                            {BUSINESS_SECTORS
                              .find(s => s.value === formData.sector?.main)
                              ?.subcategories.map((subsector) => (
                                <option key={subsector.value} value={subsector.value}>
                                  {subsector.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-200">
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="mt-1 block w-full"
                      />
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-200">
                          Contacts
                        </label>
                        <button
                          type="button"
                          onClick={handleAddContact}
                          className="text-accent-blue hover:text-accent-blue/80"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      {formData.contacts?.map((contact, index) => (
                        <div key={index} className="p-4 bg-dark-100 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <input
                              type="text"
                              value={contact.name}
                              onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                              placeholder="Nom"
                              className="w-full"
                              required
                            />
                            <input
                              type="email"
                              value={contact.email}
                              onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                              placeholder="Email"
                              className="w-full"
                              required
                            />
                            <input
                              type="tel"
                              value={contact.phone}
                              onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                              placeholder="Téléphone"
                              className="w-full"
                            />
                            <select
                              value={contact.type}
                              onChange={(e) => handleContactChange(index, 'type', e.target.value)}
                              className="w-full"
                              defaultValue={formData.type || 'brand'}
                            >
                              <option value="brand">Marque</option>
                              <option value="agency">Agence</option>
                            </select>
                            <div className="col-span-2">
                              <input
                                type="text"
                                value={contact.role || ''}
                                onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                                placeholder="Rôle (optionnel)"
                                className="w-full"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(index)}
                            className="mt-2 text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border border-transparent rounded-lg hover:bg-accent-blue/90"
                    >
                      {editingClient ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-md w-full mx-4 p-6 border border-dark-200">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">Confirmer la suppression</h3>
              <p className="mt-2 text-sm text-gray-400">
                Êtes-vous sûr de vouloir supprimer le client "{clientToDelete?.brand_name}" ? Cette action est irréversible.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Details Modal */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsDetailsModalOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedClient && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedClient.brand_name}</h2>
                    {selectedClient.sector?.main && (
                      <p className="text-gray-400">
                        {BUSINESS_SECTORS.find(s => s.value === selectedClient.sector.main)?.label}
                        {selectedClient.sector.sub && (
                          <>
                            {' • '}
                            {BUSINESS_SECTORS
                              .find(s => s.value === selectedClient.sector.main)
                              ?.subcategories.find(sub => sub.value === selectedClient.sector.sub)?.label}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setIsDetailsModalOpen(false);
                      handleEdit(selectedClient);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Contacts</h3>
                    <div className="space-y-4">
                      {selectedClient.contacts.map((contact, index) => (
                        <div key={index} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            {contact.type === 'brand' ? (
                              <Building2 className="w-5 h-5 text-accent-blue" />
                            ) : (
                              <User className="w-5 h-5 text-accent-purple" />
                            )}
                            <span className="text-white font-medium">{contact.name}</span>
                            {contact.role && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-400">{contact.role}</span>
                              </>
                            )}
                          </div>
                          <div className="space-y-2 pl-7">
                            <div className="flex items-center gap-2 text-gray-400">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${contact.email}`} className="hover:text-white">
                                {contact.email}
                              </a>
                            </div>
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-gray-400">
                                <Phone className="w-4 h-4" />
                                <a href={`tel:${contact.phone}`} className="hover:text-white">
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedClient.notes && (
                    <div>
                      <h3 className="text-lg font-medium text-white mb-4">Notes</h3>
                      <div className="bg-dark-100 rounded-lg p-4">
                        <p className="text-gray-400 whitespace-pre-wrap">{selectedClient.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}